import { Fragment, useCallback, useMemo, useState } from "react";
import { Avatar } from "@frontend/lyng/avatar/Avatar";
import { DateTime } from "luxon";
import { useTranslate } from "@tolgee/react";
import Berries from "../../assets/berries.svg?react";
import Leaves from "../../assets/leaves.svg?react";
import { useCareContext } from "../../providers";
import { useSortingOptions } from "@frontend/lyng/utils/hooks/useSortingOptions";
import { useNavigate } from "react-router-dom";
import { groupNotesByTime, type CareNote } from "./utils/notesUtils";
import { Button } from "@frontend/lyng/button";
import { Paragraph } from "@frontend/lyng/typography";
import { Edit, Plus } from "@frontend/lyng/assets/icons/24/outline";

type NotesListProps = {
  notes: CareNote[];
  hasClockedIn: boolean;
  displayView?: boolean;
};

const NoNotes = () => {
  const { t } = useTranslate();

  return (
    <div className="flex flex-col gap-8 px-2 pt-10 items-center justify-center ">
      <div className="flex flex-row items-center justify-between w-2/3 px-8">
        <Berries className="h-16 w-16" />
        <Leaves className="h-16 w-16" />
      </div>
      <Paragraph>{t("shiftDetails.visitNotes.noVisitNotes")}</Paragraph>
    </div>
  );
};

const MAX_PREVIEW_CHARS = 100; // fallback for 4 lines

export function NotesList({
  notes,
  displayView = false,
  hasClockedIn,
}: NotesListProps) {
  const { t } = useTranslate();
  const {
    state: { viewer },
  } = useCareContext();
  const navigate = useNavigate();
  const { nameOrderFn } = useSortingOptions(viewer?.tenantSettings);

  const sortedNotes = useMemo(
    () =>
      [...notes].sort(
        (a, b) =>
          DateTime.fromISO(b.createdAt ?? "").toMillis() -
          DateTime.fromISO(a.createdAt ?? "").toMillis(),
      ),
    [notes],
  );

  const grouped = useMemo(() => groupNotesByTime(sortedNotes), [sortedNotes]);

  const [expanded, setExpanded] = useState<{ [id: string]: boolean }>({});
  const toggleExpand = useCallback(
    (id: string) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] })),
    [],
  );

  const sortedGrouped = useMemo(() => {
    return Object.entries(grouped).sort(([timeA], [timeB]) =>
      timeB.localeCompare(timeA),
    );
  }, [grouped]);

  return (
    <div className="w-full flex flex-col gap-2 px-3">
      <div className="w-full px-2 pb-5">
        {hasClockedIn && !displayView && (
          <Button
            className="w-full flex items-center justify-center bg-main font-poppins font-bold rounded-xl border border-greyscale-700 shadow hover:bg-greyscale-900 transition py-3 mt-4"
            onClick={() => {
              navigate("./note/add");
            }}
            text={t("visitDetails.addVisitNote")}
            icon={Plus}
            iconPosition="left"
            variant="quaternary"
          />
        )}
      </div>
      {notes.length === 0 ? (
        <NoNotes />
      ) : (
        <div className="relative">
          <div className="flex flex-col gap-6">
            {sortedGrouped.map(([time, notes]) => (
              <div key={time} className="flex flex-col gap-2 relative">
                <div className="flex items-center mb-1 relative">
                  <div className="w-8 flex justify-center items-center">
                    <span className="w-3 h-3 rounded-full bg-greyscale-500 border-2 border-main" />
                  </div>
                  <Paragraph size="s" className="text-greyscale-500 ml-2">
                    {time}
                  </Paragraph>
                </div>
                {notes.map((note) => {
                  const isLong = note.note.length > MAX_PREVIEW_CHARS;
                  const isExpanded = !!expanded[note.id];
                  const author = note?.author ?? {
                    firstName: t("caregiver"),
                    lastName: "",
                  };
                  const preview =
                    isLong && !isExpanded
                      ? note.note.slice(0, MAX_PREVIEW_CHARS) + "..."
                      : note.note;
                  return (
                    <Fragment key={note.id}>
                      <div className="absolute left-4 top-8 bottom-0 w-px bg-greyscale-800 pb-10" />
                      <div className="flex flex-row items-start gap-2 w-full">
                        <div className="w-8 flex justify-center" />
                        <div className="bg-greyscale-800 rounded-2xl p-4 flex flex-col gap-2 w-full shadow-sm">
                          <div className="flex items-center gap-3 mb-1">
                            {!displayView && (
                              <Avatar
                                firstName={
                                  note?.author?.firstName ?? t("caregiver")
                                }
                                lastName={note?.author?.lastName ?? ""}
                                size="md"
                                randomizeColor
                              />
                            )}
                            <div className="flex flex-col flex-1 min-w-0">
                              {!displayView && (
                                <Paragraph
                                  size="m"
                                  type="primary"
                                  className="text-label-primary truncate"
                                >
                                  {nameOrderFn(author)}
                                </Paragraph>
                              )}
                              {note?.careRecipient && (
                                <Paragraph
                                  size="xxs"
                                  type="secondary"
                                  className="text-label-secondary truncate"
                                >
                                  fyrir {nameOrderFn(note.careRecipient)}
                                </Paragraph>
                              )}
                            </div>
                            {!displayView && note.author?.id === viewer?.id && (
                              <Button
                                onClick={() => {
                                  navigate({
                                    pathname: `./note/${note.id}`,
                                    search: note.careRecipient?.id
                                      ? `?noteCareRecipientId=${note.careRecipient.id}`
                                      : "",
                                  });
                                }}
                                className="ml-2 p-1 text-label-secondary hover:text-label-primary"
                                aria-label="Edit note"
                                size="sm"
                                icon={Edit}
                                iconPosition="only"
                                variant="tertiary"
                              />
                            )}
                          </div>
                          <div className="text-label-primary whitespace-pre-line relative">
                            <Paragraph
                              size="s"
                              className={
                                isLong && !isExpanded
                                  ? "line-clamp-4"
                                  : undefined
                              }
                            >
                              {preview}
                            </Paragraph>
                            {isLong && !isExpanded && (
                              <div className="absolute -bottom-3 right-2">
                                <Button
                                  onClick={() => toggleExpand(note.id)}
                                  className="text-xs font-medium bg-greyscale-800"
                                  text={t("common.seeMore")}
                                  variant="tertiary"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
